import React, { Component } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { RouterActions } from '@atlassian/react-resource-router';
import {
	type BeforeNavigateCallbackLegacy,
	createMonolithNavigationBridge,
	type ServiceDeskNavigationBridge,
} from '../bridge';
import messages from './messages';
import NavigationPrompt, { type BeforeNavigateCallback } from './prompt';

export type Props = {
	onSave: (arg1: () => void, arg2: UIAnalyticsEvent) => void;
	fireOnSaveAnalytics: boolean;
	onDiscard: () => void;
	isDirty: boolean;
	withSaveButton: boolean;
	prompt: MessageDescriptor;
};

export const onUnload = (beforeNavigateCallback: BeforeNavigateCallback): (() => void) => {
	window.addEventListener('beforeunload', beforeNavigateCallback);
	return window.removeEventListener.bind(window, 'beforeunload', beforeNavigateCallback);
};

/**
 * A reminder / alert modal shown when we prevent user from navigating away from the current page. Says "You have unsaved changes".
 * Shown e.g. in Edit report page, if something was changed and user tries no go to another page.
 */
// eslint-disable-next-line jira/react/no-class-components
export default class JsdNavigationBlocker extends Component<Props> {
	static defaultProps: Partial<Props> = {
		prompt: messages.promptText,
		withSaveButton: true,
		// Replace with lodash/noop
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		onDiscard: () => {},
	};

	navigationBridge: ServiceDeskNavigationBridge = createMonolithNavigationBridge();

	registerLegacyBlock = (beforeNavigateCallback: BeforeNavigateCallbackLegacy): (() => void) => {
		this.navigationBridge.block(beforeNavigateCallback);
		return () => {
			this.navigationBridge.unblock();
			this.navigationBridge.disable();
			// @ts-expect-error - TS2769 - No overload matches this call.
			window.removeEventListener('beforeunload', beforeNavigateCallback);
		};
	};

	render() {
		if (__SPA__) {
			return (
				<RouterActions>
					{({ registerBlock }) => (
						<NavigationPrompt onRegisterBlock={registerBlock} onUnload={onUnload} {...this.props} />
					)}
				</RouterActions>
			);
		}

		return (
			<NavigationPrompt
				onRegisterLegacyBlock={this.registerLegacyBlock}
				onUnload={onUnload}
				onRejectNavigation={this.navigationBridge.rejectNavigation}
				onConfirmNavigation={this.navigationBridge.confirmNavigation}
				{...this.props}
			/>
		);
	}
}
